import axios, { AxiosResponse } from 'axios';
import qs from 'qs';
import lockr from 'lockr';
import { isUndefined, isEmpty, isObject } from 'underscore';
import appConstants from '../constants';
// load mock data if enabled
import './mock';

const {
    API_URL,
    AUTH_KEY,
    BACKEND,
    GOOGLE_MAPS_KEY,
    LOCALSTORAGE_EXPIRES_KEY,
} = appConstants;

// ----- Helpers ----- //
// Get Api Url
const getApiUrl = () => `${API_URL}/api`;

// Get hostname url
const getBaseUrl = () => API_URL;

// defaults for axios
// WP7 (W1): withCredentials sends/receives the HttpOnly access-token cookie that
// now carries auth — the token is never read by or stored in JS.
const api = axios.create({
    baseURL: getApiUrl(),
    withCredentials: true,
});

// WP7 (W1): when the access-token cookie expires or is rejected (401), drop the
// local "logged in" flag so the route guard returns the user to login on the
// next navigation. Login failures return 422, so this never loops on /login.
api.interceptors.response.use(
    response => response,
    (error) => {
        if (error?.response?.status === 401) {
            lockr.rm(AUTH_KEY);
            lockr.rm(LOCALSTORAGE_EXPIRES_KEY);
        }
        return Promise.reject(error);
    },
);

// Get locational data
const locationData = (name:string) => axios.get(`https://maps.googleapis.com/maps/api/geocode/json?address=${name}&key=${GOOGLE_MAPS_KEY}`);

interface payloadProps {
    [key: string]: []|object|null|undefined|string|number;
}
// Format params for api call
const formatParams = (payload: payloadProps, key:number|string) => {
    let params = payload;
    if (!isObject(payload)) {
        params = {};
        params[key || 'id'] = payload;
    }
    return params;
};

// ----- Auth State Helpers -----//

/**
 * WP7 (W1): the access token now lives in an HttpOnly cookie the browser sends
 * automatically, so it is never read by or stored in JS. AUTH_KEY holds only a
 * non-sensitive "logged in" flag, used by the route guard and the authed-request
 * gate below.
 * @return string|boolean|undefined
 */
const getAuthKey = () => lockr.get(AUTH_KEY);

// ----- Api Functions ----- //
interface OptsProps {
    method: string;
    url: string;
    body?: object;
    params?: object;
    headers?: object;
    data?: string|object|null|undefined;
}
function fetchApi(opts:OptsProps, headers:object|null = null):Promise<AxiosResponse> {

    const data = (opts.method.toUpperCase() === 'GET') ? null : opts.body;
    const processedData = BACKEND === 'yii' && data ? qs.stringify(data) : data;
    const laravelHeaders = BACKEND !== 'yii' ? {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
    } : {};

    const options:OptsProps = {
        method: opts.method,
        url: opts.url,
        params: opts.params || {},
        headers: laravelHeaders,
    };

    if (opts.method.toUpperCase() !== 'GET') {
        options.data = processedData;
    }

    if (!isEmpty(headers)) {
        options.headers = { ...options.headers, ...headers };
    }

    return api(options);
}

function fetchApiAuth(opts:OptsProps):Promise<AxiosResponse> {
    return new Promise((resolve, reject) => {
        if (isUndefined(getAuthKey())) {
            reject(new Error('not-authorised'));
        } else {
            const authDate = new Date();
            lockr.set(LOCALSTORAGE_EXPIRES_KEY, authDate);
            // WP7 (W1): no Authorization header — the HttpOnly cookie carries auth.
            resolve(fetchApi(opts));
        }
    });
}

export {
    getApiUrl,
    getBaseUrl,
    locationData,
    formatParams,
    // -- Auth State Helpers --//
    getAuthKey,

    fetchApi,
    fetchApiAuth,
};
